import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../contexts/Auth';
import { Loader } from '../../components/Loader/Loader';
import { ReactComponent as Logo } from '../../assets/osavul-full.svg';
import {
  AuthorizationExpiredError,
  McpAccessDeniedError,
  OAuthScope,
  PendingAuthorization,
  SessionExpiredError,
  getPendingAuthorization,
  postAuthorizationDecision,
} from '../../utils/oauth';
import styles from './OAuthAuthorizePage.module.scss';

type TerminalReason = 'invalid_link' | 'expired' | 'no_mcp_access' | 'unavailable';

/**
 * The API sends each scope with an English label. Translating that *string* would
 * hide the keys from extraction and break the moment the backend rewords a label,
 * so the copy is keyed off the scope id — which is a stable identifier — and every
 * key is a literal a translator (or a grep) can find.
 *
 * Mirrors SCOPE_LABELS in the API's `app/domain/value_objects/oauth_scope.py`. An
 * unknown scope falls back to whatever label the API sent, so a newly added scope
 * is still described rather than rendered blank.
 */
function useScopeLabel() {
  const { t } = useTranslation();

  return (scope: OAuthScope): string => {
    switch (scope.scope) {
      case 'narratives:list':
        return t('See the list of your cases');
      case 'narratives:read':
        return t('Read your cases and their analytics');
      case 'messages:list':
        return t('Read messages inside your cases');
      case 'messages:search':
        return t('Search messages across your workspace');
      case 'sources:list':
        return t('See the list of your sources');
      case 'sources:read':
        return t('Read source and actor profiles');
      case 'source_groups:list':
        return t('See the list of your source groups');
      case 'menu:custom_dashboards':
        return t('Read your custom dashboards');
      default:
        return scope.label;
    }
  };
}

function terminalReasonFor(error: unknown): TerminalReason {
  if (error instanceof AuthorizationExpiredError) return 'expired';
  if (error instanceof McpAccessDeniedError) return 'no_mcp_access';
  return 'unavailable';
}

/**
 * OAuth consent screen. Reached only via the API's redirect from
 * `GET /oauth-consent`, which hands us an opaque `request_id`.
 *
 * The page lives outside the authenticated Layout and resolves its own auth
 * state, so an unauthenticated user is sent through the *existing* login flow
 * (password, TOTP, workspace-mandated enrolment) and comes back here rather
 * than being dropped on the dashboard.
 */
export function OAuthAuthorizePage() {
  const { t } = useTranslation();
  const scopeLabel = useScopeLabel();
  const location = useLocation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  // Opaque. Read it, pass it back to the API, never interpret it.
  const requestId = searchParams.get('request_id');

  const [pending, setPending] = useState<PendingAuthorization | null>(null);
  const [loading, setLoading] = useState(true);
  const [terminal, setTerminal] = useState<TerminalReason | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A failed *submission* is recoverable and must not replace the page: the request is
  // still valid server-side, so the user can simply press the button again. Only a
  // genuinely dead request (expired / not entitled) is terminal.
  const [decisionError, setDecisionError] = useState(false);

  const authenticated = Boolean(auth?.token);
  const { clearSession } = auth;

  const headingRef = useRef<HTMLHeadingElement>(null);
  const ready = !loading && Boolean(pending);

  useEffect(() => {
    // The consent screen swaps in where the spinner was, leaving focus on <body>.
    // A keyboard or screen-reader user would have to go looking for the decision
    // they are being asked to make; put them at the top of it instead.
    if (ready) headingRef.current?.focus();
  }, [ready]);

  useEffect(() => {
    if (!authenticated) return;

    if (!requestId) {
      setTerminal('invalid_link');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getPendingAuthorization(requestId)
      .then((data) => {
        if (cancelled) return;
        setPending(data);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof SessionExpiredError) {
          clearSession();
          return;
        }
        setTerminal(terminalReasonFor(error));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authenticated, clearSession, requestId]);

  const decide = useCallback(
    async (approve: boolean) => {
      if (!requestId || submitting) return;

      // Double-submit guard: a second approve consumes an already-consumed
      // request and 404s, which would surface as a bogus "expired" message.
      setSubmitting(true);
      setDecisionError(false);

      try {
        const scopes = approve ? (pending?.scopes ?? []).map((s) => s.scope) : [];
        const { redirect_url } = await postAuthorizationDecision(requestId, approve, scopes);
        window.location.assign(redirect_url);
      } catch (error) {
        if (error instanceof SessionExpiredError) {
          clearSession();
          return;
        }
        // Expired or not-entitled means the request is genuinely gone — nothing to retry.
        // Anything else (a 500, a dropped connection) left the request intact, so keep the
        // consent screen up and let the user try again rather than stranding them with a
        // dead end whose only escape is restarting from their MCP client.
        if (error instanceof AuthorizationExpiredError || error instanceof McpAccessDeniedError) {
          setTerminal(terminalReasonFor(error));
        } else {
          setDecisionError(true);
        }
        setSubmitting(false);
      }
    },
    [clearSession, pending, requestId, submitting],
  );

  // An authorization URL with no request_id is broken however the user got here, and no
  // amount of signing in will fix it. Say so before asking for credentials — prompting for
  // a password to service a malformed link trains exactly the habit a phishing page wants.
  if (!requestId) {
    return <TerminalState reason="invalid_link" />;
  }

  // Not authenticated: hand off to the existing login page, carrying the full
  // path *and query string* so `request_id` survives the round trip.
  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (terminal) {
    return <TerminalState reason={terminal} />;
  }

  if (loading || !pending) {
    return (
      <div className={styles.page}>
        <div className={styles.card} data-testid="oauth-loading" role="status">
          <Loader />
          {/* The spinner is a graphic with no text; without this the wait is silence. */}
          <span className={styles.srOnly}>{t('Loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Logo className={styles.logo} aria-hidden="true" />

        <h1 className={styles.title} tabIndex={-1} ref={headingRef}>
          {t('Authorize')}{' '}
          <span className={styles.client} data-testid="oauth-client-name">
            {pending.client_name}
          </span>
        </h1>
        <p className={styles.lede}>
          {t('This application is asking for access to your Osavul data.')}
        </p>

        <dl className={styles.facts}>
          <div className={styles.fact}>
            <dt className={styles.factKey}>{t('Will send you back to')}</dt>
            <dd className={styles.factValue} data-testid="oauth-redirect-host">
              {pending.redirect_host}
            </dd>
          </div>
          <div className={styles.fact}>
            <dt className={styles.factKey}>{t('Workspace')}</dt>
            <dd className={styles.factValue} data-testid="oauth-workspace-name">
              {pending.workspace_name}
            </dd>
          </div>
        </dl>

        <h2 className={styles.scopesTitle}>{t('It will be able to:')}</h2>

        {pending.scopes.length > 0 ? (
          <ul className={styles.scopes}>
            {/* Rendered exactly as returned: the API has already intersected the
                request with the workspace's entitlements. Never filter here. */}
            {pending.scopes.map((scope) => (
              <li className={styles.scope} key={scope.scope}>
                <span className={styles.scopeLabel}>{scopeLabel(scope)}</span>
                <span className={styles.scopeName}>{scope.scope}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyScopes}>{t('No permissions are being granted.')}</p>
        )}

        {decisionError && (
          <p className={styles.decisionError} role="alert">
            {t('We could not submit your decision. Please try again.')}
          </p>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className="secondary"
            disabled={submitting}
            onClick={() => decide(false)}
          >
            {t('Deny')}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => decide(true)}
          >
            {t('Allow access')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Every message is a literal argument to `t`. It used to be a lookup table of bare
 * strings passed in as variables, which no extraction pass could see — so none of
 * this screen's copy was ever translated, and a Ukrainian user read an English page
 * while granting a third party access to their data.
 */
function useTerminalMessage() {
  const { t } = useTranslation();

  return (reason: TerminalReason): string => {
    switch (reason) {
      case 'invalid_link':
        return t('This authorization link is invalid. Start again from your MCP client.');
      case 'expired':
        return t('This authorization request has expired. Start again from your MCP client.');
      case 'no_mcp_access':
        return t('This workspace is not enabled for MCP access. Contact your Osavul manager.');
      case 'unavailable':
      default:
        return t('We could not load this authorization request. Please try again.');
    }
  };
}

function TerminalState({ reason }: { reason: TerminalReason }) {
  const { t } = useTranslation();
  const terminalMessage = useTerminalMessage();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // This replaces the button the user just pressed, so focus would otherwise be
    // orphaned on a removed element. `role="alert"` announces the failure; moving
    // focus here is what lets the user read the rest of it.
    headingRef.current?.focus();
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.card} role="alert" data-testid={`oauth-terminal-${reason}`}>
        <Logo className={styles.logo} aria-hidden="true" />
        <h1 className={styles.terminalTitle} tabIndex={-1} ref={headingRef}>
          {t('Authorization failed')}
        </h1>
        <p className={styles.terminalMessage}>{terminalMessage(reason)}</p>
      </div>
    </div>
  );
}
